import { UniqueIdentifier } from '@dnd-kit/core';

import {
  ELogType,
  ICashOrder,
  IComment,
  IPaymentHistoryItem,
  IRole,
  IUser,
  IWorker,
  TCommentEntityType,
  TPayment,
  TWorker,
  IServerPriceList,
  IBooking,
  EBookingStatus,
  BookingHostGroup,
  BookingHost,
  ESyncScope,
  ApplicationType,
  EGoodsAmountOperating,
  EPromoScopeType,
  EPromoValueType,
  EPromoCampaignGenerationType,
} from 'types';

import {
  ESortDirection,
  GroupTitle,
  TClient,
  UserGroup,
} from '../pages/Clients/interfaces';

/** Auth */

export interface IGetNewTokensRequest {
  input: {
    refresh_token: string;
  };
}

/** ActiveWorkshiftMoney */
export interface IGetWorkShiftMoneyResponse {
  getWorkShiftPaymentOverviewData: {
    id: number;
    cash: number;
    card: number;
    sum: {
      good: number;
      service: number;
      tariff: number;
      deposit: number;
      tips: number;
    };
    total: number;
    cash_on_start: number;
    deposit: number;
    online_deposit: number;
    bonus: number;
    refunded: number;
    cash_orders: Array<Pick<ICashOrder, 'sum' | 'type'>>;
    worker: Pick<
      IWorker,
      | 'uuid'
      | 'first_name'
      | 'last_name'
      | 'middle_name'
      | 'roles'
      | 'phone'
      | 'nickname'
      | 'avatar_url'
    >;
    created_at: string;
    finished_at?: string;
  } | null;
}

export interface IGetWorkShiftMoneyRequest {
  id?: number;
}

/** Clients */
export interface IGetUserByIdRequest {
  uuid: string;
}

export interface IGetUserByIdResponse {
  user: Required<IUser>;
}

export interface ISetUserDepositRequest {
  input: {
    client_uuid: string;
    value: number;
  };
}

export interface ISetUserDiscountRequest {
  input: {
    client_uuid: string;
    value: number;
  };
}

export interface ISetUserDiscountResponse {
  setUserDiscount: {
    uuid: string;
    user_discount: number;
  };
}
export interface ISetUserDepositResponse {
  setDeposit: {
    uuid: string;
    deposit: number;
  };
}

export interface IBanClientRequest {
  uuid: string;
  input: {
    value: boolean;
  };
}
export interface IBanClientResponse {
  banClient: {
    uuid: string;
    banned_at: string | null;
  };
}

export interface IGetUserIdRequest {
  uuid: string;
}

export interface IGetUserIdResponse {
  user: {
    uuid: string;
    id: number;
  };
}

export interface IGetClientPaymentsRequest {
  uuid: string;
  first?: number;
  page?: number;
}

export interface IGetClientPaymentsResponse {
  getPaymentsByClientId: {
    paginatorInfo: {
      currentPage: number;
      lastPage: number;
    };
    data: Array<IPaymentHistoryItem>;
  };
}
export interface IClientsResponse {
  clients: {
    total_deposits: number;
    paginatorInfo: {
      total: number;
      count: number;
      currentPage: number;
      lastPage: number;
    };
    data: TClient[];
  };
}

export interface IClientsRequest {
  input?: {
    q?: string;
    sort?: {
      field: string;
      direction: ESortDirection;
    };
    filter?: {
      groupUUID: string;
    };
  };
  page?: number;
  first?: number;
}

export type TTotalClientsResponse = {
  clients: {
    total_deposits: number;
    paginatorInfo: {
      total: number;
    };
  };
};
export type TGroupsTitlesResponse = {
  userGroups: GroupTitle[];
};

export type TUserGroupsResponse = {
  userGroups: UserGroup[];
};
export type TChangeUsersGroupRequest = {
  input: {
    group_uuid?: string;
    users_uuids?: string[];
  };
};

export type TChangeUsersGroupResponse = {
  changeUsersGroup: UserGroup;
};
export type UserGroupInput = {
  discount: number;
  title: string;
};

export type CreateUserGroupRequest = {
  input: UserGroupInput;
};

export type CreateUserGroupResponse = {
  createUserGroup: UserGroup;
};

export type UpdateUserGroupRequest = {
  uuid: UserGroup['uuid'];
  input: UserGroupInput;
};

export type UpdateUserGroupResponse = {
  updateUserGroup: UserGroup;
};

export type DeleteUserGroupRequest = {
  uuid: UserGroup['uuid'];
};

export type DeleteUserGroupResponse = {
  deleteUserGroup: UserGroup;
};

/** Comments */

export interface IDeleteCommentsRequest {
  id: number;
}

export interface IDeleteCommentsResponse {
  deleteComment: {
    id: number;
  };
}

export interface ICreateCommentsRequest {
  input: {
    text: string;
    type: TCommentEntityType;
    entity_id: number;
    is_important?: boolean;
  };
}

export interface ICreateCommentsResponse {
  createComment: {
    id: number;
  };
}

export interface IUpdateCommentsRequest {
  id: number;
  input: {
    is_important?: boolean;
  };
}

export interface IUpdateCommentsResponse {
  updateComment: {
    id: number;
  };
}

export interface IGetCommentsRequest {
  input: {
    entity_id: number;
    type: TCommentEntityType;
  };
}

export interface IGetCommentsResponse {
  comments: { data: Array<IComment> };
}

/** CurrentTariff */
export interface IGetActiveTariffRequest {
  input: {
    client_session_id: number;
  };
}

export interface IGetActiveTariffResponse {
  getCurrentTariff: {
    id: number;
    title: string;
    cost: number;
    has_fixed_finish_time: boolean;
  };
}

/** Events*/
export interface IGetEventsRequest {
  input?: {
    q?: string;
    types?: Array<ELogType>;
    start?: string;
    end?: string;
    work_shift_id?: number;
  };
  page?: number;
  first?: number;
}

export interface ILogUserData {
  phone: string;
  nickname?: string;
  first_name?: string;
  last_name?: string;
  uuid: string;
}

export interface IEventListItem {
  timestamp: string;
  type: ELogType;
  description: string;
  host: {
    title: string;
  };
  host2: {
    title: string;
  };
  client: ILogUserData | null;
  worker: ILogUserData;
  role: {
    id: number;
    title: string;
  };
  role2: {
    title: string;
  };
  comment: string;
  payment: {
    id: number;
    title: string;
    value: string;
  };
  value1: number;
  value2: number;
  penalty: number;
  booking: {
    cost: number;
    from: string;
    id: number;
    to: string;
  };
  hosts: {
    value: number;
    title: string;
    id: number;
  }[];
  tariff: {
    id: number;
    title: string;
  };
  cash_order: {
    type: 'PKO' | 'RKO';
    sum: number;
    comment: string;
  };
  client_session: {
    elapsed: number;
    expires_at: string;
    finished_at: string;
    id: number;
    time_left: number;
    is_per_minute: boolean;
    total_cost: number;
  };
  shortcut: {
    title: string;
  };
  deposit: number;
  duration: number;
  combo: {
    host_group_id: number;
    host_group_title: string;
    id: number;
    tariff_id: number;
    tariff_title: string;
    title: string;
  };
  combo_items: {
    title: number;
    price: number;
    amount: number;
  }[];
  discount: {
    title: string;
    value: number;
  };
  good: {
    title: string;
  };
  goods: {
    title: string;
  }[];
  host_group: {
    title: string;
  };
  new_devices: {
    alias: string;
    value: string;
    value2: string;
  }[];
  operator: {
    first_name: string;
    last_name: string;
  };
  cash_on_start: number;
  promo_code_item: {
    id: number;
    title: string;
    description: string;
    value: string;
    applicable_params: string;
    client_scope: string;
    scopes: string;
    active: boolean;
    active_from: string;
    active_to: string;
    is_tracked: boolean;
    created_at: string;
  };
  promo_campaign: {
    id: number;
    title: string;
    description: string;
    value: string;
    applicable_params: string;
    client_scope: string;
    scopes: string;
    generation_settings: string;
    counters: string;
    active: string;
    active_from: string;
    active_to: string;
    is_tracked: boolean;
    created_at: string;
    updated_at: string;
  };
  company_news_item: {
    id: string;
    image: string;
    label: string;
    url: string;
    title: string;
    active: boolean;
    active_from: string;
    active_to: string;
    created_at: string;
  };
  company_news_item_changes: {
    id: string;
    image: string;
    label: string;
    url: string;
    title: string;
    active: boolean;
    active_from: string;
    active_to: string;
    created_at: string;
  };
  manager: {
    nickname: string;
    phone: string;
    first_name: string;
    last_name: string;
    uuid: string;
  };
  service: {
    title: string;
  };
  setting: {
    alias: string;
    value: string;
    value2: string;
  };
  user_group: {
    title: string;
    value: number;
  };
  user_group2: {
    title: string;
    value: number;
  };
  worker_task: {
    title: string;
  };
  host_command: {
    title: string;
  };
  cashback: number;
  payment_items: {
    amount: number;
    entity_type: string;
    sum: number;
    title: string;
    id: number;
  }[];
  payment_transaction: {
    payment_id: number;
    uuid: string;
  };
  warehouse_item: {
    value2: number;
    value: number;
    title: string;
    entity_type: string;
  };
  additional_seconds: number;
  send_code_method: string;
  goods_category: {
    title: string;
  };
  additional_data: string;
  advertisement: {
    id: string;
    title: string;
  };
}

export interface IGetEventListResponse {
  eventList: {
    paginatorInfo: {
      count: number;
      currentPage: number;
      lastPage: number;
    };
    data: Array<IEventListItem> | null;
  };
}
/** HostGroups */
export interface IHostGroupsResponse {
  hostGroups: Array<{
    id: number;
    title: string;
    customization: {
      color: string;
    };
  }>;
}

/** Hosts */
export interface IRedeemBookingRequest {
  id: number;
}

export interface IRedeemBookingResponse {
  setBookingStatus: {
    id: number;
  };
}

export interface ITariffsResponse {
  data: {
    id: number;
    per_minute: boolean;
    use_global_discounts: boolean;
    price_list: Array<IServerPriceList>;
  };
}

export interface IDeleteHostResponse {
  deleteHost: {
    id: number;
    alias: string;
    in_service: boolean;
  };
}

export interface IDeleteHostGroupResponse {
  deleteHostGroup: {
    id: number;
    title: string;
  };
}

export interface ISetServiceModeResponse {
  setHostServiceMode: {
    id: number;
    in_service: boolean;
    alias: string;
  };
}

export interface IHostTypeResponse {
  hostTypes: Array<{
    id: number;
  }>;
}

export interface ISendWakePacketRequest {
  input: {
    host_id: number;
  };
}

export interface ISendWakePacketResponse {
  sendWakePacket: boolean;
}

export interface ISendWakePacketsRequest {
  input: {
    host_ids: Array<number>;
  };
}

export interface ISendWakePacketsResponse {
  sendWakePacket: boolean;
}

export interface IMovedHost {
  id: number;
  coord_x: number;
  coord_y: number;
}

export interface IMoveHostsRequest {
  input: Array<IMovedHost>;
}

export enum EClientSessionStatus {
  PLANED = 'PLANED',
  ACTIVE = 'ACTIVE',
  FINISHED = 'FINISHED',
  CANCELLED = 'CANCELLED',
}

export interface IStartClientSessionResponse {
  startClientSession: {
    status: EClientSessionStatus;
    hosts: { host: { alias: string } }[];
  };
}

export interface IStartClientSessionRequest {
  input: {
    host_id: number;
    post_paid: boolean;
  };
}

export interface IAuthenticateShellClientResponse {
  authenticateShellClient: {
    id: number;
    user: {
      uuid: string;
      nickname: string;
      phone: string;
    } | null;
  };
}

export interface IAuthenticateShellClientRequest {
  hostId: number;
  clientUuid: string;
}

export interface IGetHostsForGameAccountResponse {
  hosts: Array<{
    group_id: number;
    id: number;
    alias: string;
    position: number;
  }>;
  hostGroups: Array<{
    id: number;
    title: string;
  }>;
}

export interface IHostsMacAddresses {
  hostOverview: {
    mac_addr: string;
  };
}

/** Payment */
//TODO Доделать интерфейсы запроса

/** Tasks */
//TODO Доделать интерфейсы запроса

/** Versions */
export interface IVersionsResponse {
  versions: { easter_egg: string; backend: string };
}

/** Workers */
export interface IGetRolesResponse {
  roles: Array<Pick<IRole, 'id' | 'alias' | 'permissions'>>;
}

/** Register */
export enum EConfirmCodeType {
  REGISTER = 'REGISTER',
  RESET_PASSWORD = 'RESET_PASSWORD',
  WITHDRAW = 'WITHDRAW',
}

export enum ESendConfirmationCodeMethods {
  CASCADE = 'CASCADE',
  EMAIL = 'EMAIL',
  TELEGRAM = 'TELEGRAM',
}

export interface ISendExtendConfirmationCodeRequest {
  input: {
    method?: ESendConfirmationCodeMethods;
    phone: string;
    type: EConfirmCodeType;
  };
}
export interface ISendExtendConfirmationCodeResponse {
  organizationSendExtendConfirmationCode: boolean;
}

export interface IVerifyClientRequest {
  input: {
    code: string;
    verification_id: string | null;
  };
}
export interface IVerifyClientResponse {
  verifyClient: {
    id: number;
    uuid: number;
  };
}

export interface IGetWorkersPageInfo {
  workers: {
    data: Array<TWorker>;
    paginatorInfo: {
      total: number;
      count: number;
      currentPage: number;
      lastPage: number;
    };
  };
}

export interface ICreateWorkerRequest {
  input: {
    first_name: string;
    last_name: string;
    middle_name: string;
    phone: string;
    role_ids: number[];
  };
}

export interface ICreateWorkerResponse {
  createWorker: TWorker;
}

export interface IUpdateWorkerRequest {
  id: number;
  input: {
    first_name: string;
    last_name: string;
    middle_name: string;
    role_ids: number[];
  };
}

export interface IUpdateWorkerResponse {
  updateWorker: TWorker;
}

export interface IDeleteWorkerRequest {
  id: number;
}

export interface IDeleteWorkerResponse {
  deleteWorker: {
    uuid: string;
    id: number;
  };
}

export type TPausableClientSessions = {
  duration: number;
  elapsed: number;
  expires_at: string;
  id: number;
  status: 'PLANED' | 'ACTIVE' | 'FINISHED' | 'CANCELLED';
  host_group: {
    title: string;
  };
  tariff: {
    title: string;
    schedules: {
      start: number;
      end: number;
    }[];
  };
};

export interface IPausableClientSessionsResponse {
  pausableClientSessions: TPausableClientSessions[];
}

export interface IPausableClientSessionsRequest {
  clientId: string;
}

/** BONUS */
export interface ISetBonusResponse {
  setBonus: {
    uuid: string;
    bonus: number;
  };
}
export interface ISetBonusRequest {
  input: {
    client_uuid: string;
    value: number;
  };
}

export enum EBonusType {
  ACHIEVEMENT = 'ACHIEVEMENT',
  CASHBACK = 'CASHBACK',
  MANAGER = 'MANAGER',
  PROMOCODE = 'PROMOCODE',
  USED = 'USED',
  REFUND = 'REFUND',
}

export type TBonusItem = {
  type: EBonusType;
  worker_first_name: string;
  worker_last_name: string;
  created_at: string;
  add: boolean;
  new_value: number;
  old_value: number;
  delta: number;
};

export interface IGetBonusHistoryResponse {
  getBonusHistory: {
    data: Array<TBonusItem>;
    paginatorInfo: {
      currentPage: number;
      lastPage: number;
    };
  };
}
export interface IGetBonusHistoryRequest {
  uuid: string;
  first?: number;
  page?: number;
}

/** TRANSFERS */
export enum EDepositTransferOperationType {
  DEPOSIT = 'DEPOSIT',
  WITHDRAWAL = 'WITHDRAWAL',
}

export type TClientClub = {
  id: number;
  name: string;
  address: string;
};
export type TTransfersItem = {
  type: EDepositTransferOperationType;
  created_at: string;
  sum: number;
  club: TClientClub;
};
export interface IGetClientDepositTransfersResponse {
  getClientDepositTransfers: {
    data: Array<TTransfersItem>;
    paginatorInfo: {
      currentPage: number;
      lastPage: number;
    };
  };
}
export interface IGetClientDepositTransfersRequest {
  uuid: string;
  first?: number;
  page?: number;
}

export interface IPostPaymentsCount {
  activeWorkShift: {
    payments: Array<Pick<TPayment, 'method' | 'status' | 'id'>> | null;
  } | null;
}

/** BOOKINGS */
export interface BookingDataResponse {
  hostGroupsOverview: Array<BookingHostGroup>;
  hostsOverview: Array<BookingHost>;
}

export interface IGetBookingRequest {
  id: number;
}
export interface IGetBookingResponse {
  getBooking: IBooking;
}

interface IBookingInput {
  from: string;
  to: string;
  hosts: Array<number>;
  client?: string;
  comment?: string;
  status?: EBookingStatus;
}

export interface ICreateBookingRequest {
  input: IBookingInput;
}
export interface ICreateBookingResponse {
  createBooking: {
    id: number;
  };
}

export interface IUpdateBookingRequest {
  id: number;
  input: IBookingInput;
}
export interface IUpdateBookingResponse {
  updateBooking: Pick<IBooking, 'status'>;
}

/** workShiftReport */
export interface IDetailedWorkShiftReport {
  id: number;
  worker: Pick<IWorker, 'uuid' | 'first_name' | 'last_name' | 'roles'>;
  created_at: string;
  finished_at: string;
  money: {
    sum: {
      cash: number;
      card: number;
      deposit: number;
      bonus: number;
    };
    cash_on_start: number;
  };
  payments: Array<TPayment>;
}

export interface IWorkShiftByIdRequest {
  id: number;
}
export interface IWorkShiftByIdResponse {
  workShift: IDetailedWorkShiftReport;
}

export interface IWorkShiftsReportExportRequest {
  input: {
    from: string;
    to: string;
    workerUuid: string | null;
  };
}
export interface IWorkShiftsReportExportResponse {
  workShiftsReport: {
    url: string;
  };
}
export interface IWorkShiftsSummaryReportExportResponse {
  workShiftsSummaryReport: {
    url: string;
  };
}

export interface IWorkShiftsReportRequest {
  input: {
    from: string;
    to: string;
    workerUuid: string | null;
  };
}
export interface IGetWorkersResponse {
  workers: {
    data: Array<{
      id: number;
      uuid: string;
      last_name: string;
      first_name: string;
    }>;
  };
}
export interface IUnifiedReportData {
  labels: Array<string>;
  extraLabels: Array<string>;
  extraLabels2: Array<string>;
  extraLabels3: Array<string>;
  keys: Array<string>;

  data: Array<{
    values: Array<number>;
  }>;
}
export interface IWorkShiftsReportResponse {
  workShiftsReport: IUnifiedReportData;
  workShiftsSummaryReport: IUnifiedReportData;
}
export interface IGetWorkShiftsResponse {
  workShifts: {
    data: Array<{
      id: number;
      worker: Pick<IWorker, 'uuid' | 'first_name' | 'last_name' | 'roles'>;
      created_at: string;
      finished_at: string;
      money: {
        sum: {
          total: number;
        };
      };
    }>;
  };
}
export interface IGetWorkShiftDataResponse {
  getWorkShiftPaymentOverviewData: {
    id: number;
    total: number;
    cash: number;
    card: number;
    deposit: number;
    online_deposit: number;
    cash_on_start: number;
    bonus: number;
    created_at: string;
    finished_at: string;
    sum: {
      good: number;
      service: number;
      deposit: number;
      tariff: number;
      tips: number;
    };
    cash_orders: Array<
      Pick<ICashOrder, 'type' | 'comment' | 'created_at' | 'sum'>
    >;
    worker: Pick<IWorker, 'uuid' | 'first_name' | 'last_name'>;
    goods: IGoodsPrintItem[];
    combos: ICombosPrintItem[];
    services: IServicesPrintItem[];
  };
  workShift: {
    comment: string;
  };
}

export type GetWorkShiftDataRequest = {
  id: number;
};

export interface IBasePrintItem {
  title: string;
  amount: number;
  sum: number;
}

export type IServicesPrintItem = IBasePrintItem;

export type ICombosPrintItem = IBasePrintItem;

export interface IGoodsPrintItem extends IBasePrintItem {
  in_stock: number;
  cost: number;
}

// user reset password
export interface IResetUserPasswordResponse {
  resetUserPassword: {
    id: number;
    uuid: string;
  };
}

// REVIEW
export interface IOverviewReportSummaryData {
  extraString: string | null;
  extraValue: number | null;
  title: string;
  value: number;
}

export interface IOverviewReportData {
  values: number[];
}

export interface IUnifiedReport {
  data: IOverviewReportData[];
  extraLabels: string[];
  extraLabels2: string[];
  extraLabels3: string[];
  keys: string[];
  labels: string[];
  points: number;
  summary: IOverviewReportSummaryData[];
  title: EReportType;
  url: string | null;
}

export interface IOverviewReportResponse {
  overviewReport: {
    data: IUnifiedReport[];
    url: string | null;
  };
}
export interface IOverviewReportRequest {
  input: {
    start: string;
    end: string;
  };
}

export enum EReportType {
  FINANCE = 'Финансовые показатели',
  BONUSES = 'Распределение начисленных бонусов',
  CLIENTS = 'Распределение клиентов по количеству визитов',
  OPERATION = 'Операционные показатели',
  GOODS = 'Товары',
  EXPENSE = 'Распределение расходов клиентов',
}

export interface IOverviewReportItemsResponse {
  topSoldOverviewItemsReport: {
    data: IOverviewReportData[];
    extraLabels: string[];
    extraLabels2: string[];
    extraLabels3: string[];
    keys: string[];
    labels: string[];
    points: number;
    summary: IOverviewReportSummaryData[];
    title: EReportType;
    url: string | null;
  };
}

export interface IClubLoadByDayOfWeekReportResponse {
  clubLoadByDayOfWeekReport: {
    data: IOverviewReportData[];
  };
}

export interface ICreateHostPINResponse {
  createHostPIN: string;
}

export interface ICreateHostPINRequest {
  hostId: number;
}

export interface IGenerateQRResponse {
  generateQR: string;
}

export interface IGenerateQRRequest {
  input: {
    host_id: number;
  };
}

export interface IHostCommandTemplate {
  id: number;
  title?: string;
  path: string;
  arguments: string;
}

export interface ICreateHostCommandTemplateResponse {
  createHostCommandTemplate: IHostCommandTemplate;
}

export interface ICreateHostCommandTemplateRequest {
  input: Omit<Required<IHostCommandTemplate>, 'id'>;
}

export interface IUpdateHostCommandTemplateResponse {
  updateHostCommandTemplate: IHostCommandTemplate;
}

export interface IUpdateHostCommandTemplateRequest {
  input: Required<IHostCommandTemplate>;
}

export interface IDeleteHostCommandTemplateRequest {
  id: number;
}

export interface IGetHostCommandTemplates {
  hostCommandTemplates: Array<IHostCommandTemplate>;
}

export interface IExportOverviewReportResponse {
  exportOverviewReport: string;
}

export interface IExportOverviewReportRequest {
  input: {
    from: string;
    to: string;
  };
}

/** Tariffs */
export interface ITariffsFromSettingsResponse {
  tariffs: {
    data: Array<{
      id: number;
      title: string;
    }>;
  };
}

// NEWS

export interface INewsLink {
  label: string;
  url: string;
}

export interface IPublicationPeriod {
  from: string | null;
  until: string | null;
}

export interface ICompanyNewsListResponse {
  companyNewsList: {
    data: ICompanyNewsList[] | null;
    paginatorInfo: {
      count: number;
      currentPage: number;
      lastPage: number;
    };
  };
}

export interface ICompanyNewsList {
  active?: string;
  content: string;
  created_at: string;
  id?: string;
  image: string;
  link: INewsLink | null;
  publication_period: IPublicationPeriod | null;
  title: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface ICompanyNewsListRequest {
  input?: {
    active?: boolean;
  };
  page: number | null;
  first: number | null;
}

export interface ICompanyNewsRequestInput {
  active: boolean;
  content: string;
  image: string;
  link: {
    url: string;
    label: string;
  };
  publication_period: {
    until: string;
    from: string;
  };
  title: string;
}

export interface ICreateCompanyNewsRequest {
  input?: ICompanyNewsRequestInput;
}
export interface IUpdateCompanyNewsRequest {
  updateCompanyNewsId: string;
  input?: ICompanyNewsRequestInput;
}
export interface IDeleteCompanyNewsRequest {
  deleteCompanyNewsId: string;
}
export interface IGetCompanyNewsFromIdRequest {
  companyNewsArticleId: string;
}
export interface IGetCompanyNewsFromIdResponse {
  companyNewsArticle: ICompanyNewsList;
}

//Bonus
export interface IBonusDetailedTimeSeriesReport {
  data: IOverviewReportData[];
  keys: string[];
}

export interface IBonusDetailedSummaryReport {
  data: IOverviewReportData[];
  labels: string[];
  summary: IOverviewReportSummaryData[];
}
export interface IBonusDetailedSummaryReportFile {
  url: string;
}
export interface IBonusDetailedTimeSeriesReportResponse {
  bonusDetailedTimeSeriesReport: IBonusDetailedTimeSeriesReport;
}

export interface IBonusDetailedSummaryReportResponse {
  bonusDetailedSummaryReport: IBonusDetailedSummaryReport;
}
export interface IBonusDetailedSummaryReportFileResponse {
  bonusDetailedSummaryReport: IBonusDetailedSummaryReportFile;
}

export enum ETabs {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
}
export interface IBonusDetailedTimeSeriesReportRequest {
  input: {
    from: string;
    groupBy: ETabs;
    to: string;
  };
}
export interface IBonusDetailedSummaryReportRequest {
  input: {
    end: string;
    start: string;
  };
}
export interface IBonusUsersBonusReportResponse {
  usersBonusReport: {
    data: Array<{ values: Array<number> }>;
    keys: Array<string>;
    extraLabels: Array<string>;
    extraLabels2: Array<string>;
    extraLabels3: Array<string>;
    labels: Array<string>;
    url: string;
  };
}
export interface IBonusUsersBonusReportFileResponse {
  usersBonusReport: {
    url: string;
  };
}

export interface IBonusUsersBonusReportRequest {
  input: {
    from: string;
    to: string;
  };
}

// APPLICATIONS
export interface IGetShortcutGroupsResponse {
  shortcutGroups: IShortcutGroup[];
}

export interface CreateShortcutGroupResponse {
  createShortcutGroup: IShortcutGroup;
}

export interface UpdateShortcutGroupResponse {
  updateShortcutGroup: IShortcutGroup;
}

export interface DeleteShortcutGroupResponse {
  deleteShortcutGroup: Pick<IShortcutGroup, 'id'>;
}

export interface CreateShortcutGroupRequest {
  input: Omit<IShortcutGroup, 'id'>;
  sync?: ESyncScope;
}

export interface UpdateShortcutGroupRequest {
  updateShortcutGroupId: number;
  input: Omit<IShortcutGroup, 'id'>;
  sync?: ESyncScope;
}

export interface DeleteShortcutGroupRequest {
  id: number;
  sync?: ESyncScope;
}

export interface IUpdateShortcutRequest {
  id: number;
  input: TShortcutUpdateInput;
}

export interface IUpdateShortcutResponse {
  updateShortcut: Pick<IShortcut, 'id' | 'type'>;
}

export interface IUpdateSortRequest {
  input: TShortcutSortInput;
}

export interface IUpdateAllShortcutsRequest {
  updateShortcutId: number;
  updateShortcutInput: {
    group_id: number;
    sort: number;
  };
  updateShortcutSortInput: TShortcutSortInput;
}

export interface IUpdateAllShortcutsResponse {
  updateShortcut: Pick<IShortcut, 'id' | 'type'>;
  updateShortcutSort: boolean;
}
export type GroupType = 'APPLICATION' | 'GAME' | 'DEFAULT';

export interface IShortcutGroup {
  id: number;
  sort: number;
  title: string;
  groupType: GroupType;
}

export interface IShortcut {
  id: number;
  type: ApplicationType;
  title: string;
  group_id: number;
  group: IShortcutGroup;
  main_picture?: string;
  only_for_registered: boolean;
  age_rating: number | null;
  free_run: boolean;
  sort: number;
}

export type TShortcutUpdateInput = Pick<IShortcut, 'group_id' | 'sort'>;

export type TShortcutSortInput = Array<{ id: number; sort: number }>;

export interface IGetShortcutsResponse {
  shortcuts: Array<IShortcut>;
}

export interface IDeleteShortcutRequest {
  id: number;
  sync?: ESyncScope;
}

export interface IDeleteShortcutResponse {
  deleteShortcut: Pick<IShortcut, 'type'>;
}

export interface IDraggableData {
  id: UniqueIdentifier;
  original: IShortcut;
}

// GOODS AMOUNT HISTORY
export interface IGoodHistoryItem {
  comment: string;
  created_at: string;
  delta: number;
  initiator: {
    first_name: string;
    last_name: string;
    middle_name: string;
    uuid: string;
  };
  operation: EGoodsAmountOperating;
  quantity_after: number;
  [key: string]: unknown;
}

export interface IGoodHistoryData {
  data: IGoodHistoryItem[];
  paginatorInfo: {
    currentPage: number;
    lastPage: number;
  };
}

export interface IGoodsAmountHistoryResponse {
  goodHistory: IGoodHistoryData;
}

export interface IGoodsAmountHistoryRequest {
  input: {
    id?: number;
    from: string;
    to: string;
    operations?: EGoodsAmountOperating[] | null;
    page?: number | null;
  };
}

// promo-codes
export type TPromoCodeData = {
  active: boolean;
  active_from: string;
  active_to: string;
  amount: number;
  counters: {
    used: number;
  };
  applicable_params: string;
  client_scope: Array<{
    avatar_url?: string | null;
    id: string;
    phone?: string;
    title?: string;
    __typename: 'ScopedClient' | 'ScopedGroup';
  }>;
  code: string;
  created_at: string;
  id: number;
  is_tracked: boolean;
  scopes: Array<EPromoScopeType>;
  value: {
    currency: {
      alias: string;
      letter: string;
    };
    type: EPromoValueType;
    value: number;
  };
};

// promo-campaign
export type TPromoCampaignData = {
  id: string;
  title: string;
  active: boolean;
  active_from: string;
  active_to: string;
  applicable_params: string;
  client_scope: Array<{
    avatar_url?: string | null;
    id: string;
    phone?: string;
    title?: string;
    __typename: 'ScopedClient' | 'ScopedGroup';
  }>;
  counters: {
    total: number;
    used: number;
  };
  created_at: string;
  is_tracked: boolean;
  scopes: Array<EPromoScopeType>;
  generation_settings: {
    length: number;
    prefix: string;
    type: EPromoCampaignGenerationType;
  };
  value: {
    currency: {
      alias: string;
      letter: string;
    };
    type: EPromoValueType;
    value: number;
  };
};

export interface ICreatePromoCodeResponse {
  createPromoCode: TPromoCodeData;
}

export interface ICreatePromoCampaignResponse {
  createPromoCampaign: TPromoCampaignData;
}
export type PromoCodeRequestInput = {
  active: boolean;
  active_from?: string | null;
  active_to?: string | null;
  amount?: number;
  applicable_params?: {
    goods?: Array<number>;
    services?: Array<number>;
    tariffs?: Array<number>;
  } | null;
  client_scope?: {
    groups: Array<string>;
    users: Array<string>;
  } | null;
  code: string;
  is_tracked?: boolean;
  scopes?: Array<EPromoScopeType>;
  value: {
    type: EPromoValueType;
    value: number;
  };
};

export type PromoCampaignRequestInput = {
  title: string;
  active: boolean;
  active_from?: string | null;
  active_to?: string | null;
  amount?: number;
  applicable_params?: {
    goods?: Array<number>;
    services?: Array<number>;
    tariffs?: Array<number>;
  } | null;
  client_scope?: {
    groups: Array<string>;
    users: Array<string>;
  } | null;
  generation_settings?: {
    amount?: number;
    length?: number;
    prefix?: string;
    type?: EPromoCampaignGenerationType;
  };
  is_tracked?: boolean;
  scopes?: Array<EPromoScopeType>;
  value: {
    type: EPromoValueType;
    value: number;
  };
};

export interface ICreatePromoCodeRequest {
  input: PromoCodeRequestInput;
}

export interface ICreatePromoCampaignRequest {
  input: PromoCampaignRequestInput;
}

export interface IGetPromoCodeResponse {
  promoCode: TPromoCodeData;
}
export interface IGetPromoCampaignResponse {
  promoCampaign: TPromoCampaignData;
}
export interface IGetPromoCodeRequest {
  promoCodeId: number;
}
export interface IGetPromoCampaignRequest {
  promoCampaignId: string;
}
export interface IUpdatePromoCodeRequest {
  id: number;
  input: PromoCodeRequestInput;
}

export interface IUpdatePromoCampaignRequest {
  id: string;
  input: Omit<PromoCampaignRequestInput, 'value'>;
}

export interface IUpdatePromoCodeResponse {
  updatePromoCode: TPromoCodeData;
}

export interface IUpdatePromoCampaignResponse {
  updatePromoCampaign: TPromoCampaignData;
}

export interface IDeletePromoCodeRequest {
  id: number;
}

export interface IDeletePromoCampaignRequest {
  id: string;
}
export interface IDeletePromoCodeResponse {
  deletePromoCode: {
    id: number;
    code: string;
  };
}

export interface IDeletePromoCampaignResponse {
  deletePromoCampaign: {
    id: number;
    code: string;
  };
}
export interface IPromoCodesResponse {
  promoCodesList: {
    items: Array<TPromoCodeData>;
    paginatorInfo: {
      count: number;
      currentPage: number;
      lastPage: number;
      total: number;
    };
  };
}
export interface IPromoCampaignResponse {
  promoCampaignList: {
    items: Array<TPromoCampaignData>;
    paginatorInfo: {
      count: number;
      currentPage: number;
      lastPage: number;
      total: number;
    };
  };
}

export interface IGeneratePromoCodeResponse {
  generation: {
    id: string;
  };
}

export interface IGeneratePromoCodeRequest {
  id?: string | null;
  amount?: number;
}
